import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { MINISTRIES } from "@/lib/constants";
import { Panel, SButton, sInput, sLabel } from "@/components/staff/Bits";
import { localToday } from "@/components/FormBits";

export function useVisitMeta() {
  const [meta, setMeta] = useState({ visitors: [], types: {} });
  useEffect(() => { api.get("/visits/meta").then((r) => setMeta(r.data)).catch(() => {}); }, []);
  return meta;
}

export default function VisitForm({ caseId, safeguarding, minor, onDone, askCase }) {
  const meta = useVisitMeta();
  const today = localToday();
  const blank = { ref: "", date: today, time: "09:00", visitor_ids: [], team: "", visit_type: "", purpose: "", vulnerable_involved: !!minor };
  const [f, setF] = useState(blank);
  const [busy, setBusy] = useState(false);
  const set = (p) => setF((o) => ({ ...o, ...p }));
  const pool = safeguarding ? meta.visitors.filter((v) => v.role === "director" || v.safeguarding_access) : meta.visitors;
  const toggle = (id) => set({ visitor_ids: f.visitor_ids.includes(id) ? f.visitor_ids.filter((x) => x !== id) : [...f.visitor_ids, id] });

  const submit = async () => {
    setBusy(true);
    try {
      let id = caseId;
      if (!id) {
        const { data } = await api.get("/cases", { params: { q: f.ref.trim(), page_size: 1 } });
        const hit = data.items.find((c) => c.reference === f.ref.trim().toUpperCase());
        if (!hit) { toast.error("No case with that reference"); return; }
        id = hit.id;
      }
      await api.post("/visits", { case_id: id, date: f.date, time: f.time, visitor_ids: f.visitor_ids, team: f.team || null, visit_type: f.visit_type, purpose: f.purpose, vulnerable_involved: f.vulnerable_involved });
      toast.success("Visit scheduled. Visitors can now confirm it.");
      setF(blank);
      onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <Panel title="Schedule a home visit" testid="visit-form">
      <div className="grid sm:grid-cols-2 gap-3">
        {askCase && <div className="sm:col-span-2"><label className={sLabel}>Case reference</label><input data-testid="visit-case-ref-input" className={`${sInput} font-mono uppercase`} placeholder="MBC-2026-000001" value={f.ref} onChange={(e) => set({ ref: e.target.value })} /></div>}
        <div><label className={sLabel}>Date</label><input type="date" min={today} data-testid="visit-date-input" className={sInput} value={f.date} onChange={(e) => set({ date: e.target.value })} /></div>
        <div><label className={sLabel}>Time</label><input type="time" data-testid="visit-time-input" className={sInput} value={f.time} onChange={(e) => set({ time: e.target.value })} /></div>
        <div><label className={sLabel}>Reason for visit</label><select data-testid="visit-type-select" className={sInput} value={f.visit_type} onChange={(e) => set({ visit_type: e.target.value })}><option value="">Choose…</option>{Object.entries(meta.types).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></div>
        <div><label className={sLabel}>Pastoral team</label><select data-testid="visit-team-select" className={sInput} value={f.team} onChange={(e) => set({ team: e.target.value })}><option value="">—</option>{MINISTRIES.map((m) => <option key={m}>{m}</option>)}</select></div>
        <div className="sm:col-span-2"><label className={sLabel}>Purpose (no health or confidential details)</label><input data-testid="visit-purpose-input" className={sInput} value={f.purpose} onChange={(e) => set({ purpose: e.target.value })} placeholder="e.g. Prayer and Holy Communion" /></div>
        <div className="sm:col-span-2">
          <p className={sLabel}>Visitors {safeguarding && <span className="text-[#8A2E14] font-normal">(safeguarding-authorized only)</span>}</p>
          <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto">
            {pool.map((v) => (
              <button type="button" key={v.id} data-testid={`visit-visitor-${v.id}`} onClick={() => toggle(v.id)}
                className={`h-9 px-3 rounded-full text-sm font-semibold border transition-colors duration-150 ${f.visitor_ids.includes(v.id) ? "bg-[#3D6346] text-white border-[#3D6346]" : "bg-white border-[#D9CDBF] hover:bg-[#F4EBE4]"}`}>{v.name}</button>))}
          </div>
        </div>
        <label className="sm:col-span-2 flex gap-2 text-sm font-semibold"><input type="checkbox" data-testid="visit-vulnerable-checkbox" className="w-5 h-5 accent-[#B85028]" checked={f.vulnerable_involved} disabled={!!minor} onChange={(e) => set({ vulnerable_involved: e.target.checked })} />Involves a child or vulnerable adult (at least two visitors required)</label>
      </div>
      <SButton className="mt-4" variant="green" data-testid="visit-submit-button" disabled={busy || !f.visit_type || !f.purpose || f.visitor_ids.length === 0 || (askCase && !f.ref)} onClick={submit}>Schedule visit</SButton>
    </Panel>
  );
}
